import React from 'react'
import PropTypes from 'prop-types'
import reset from 'styled-reset'
import { createGlobalStyle } from 'styled-components'
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom'
import { parse } from 'query-string'
import { v4 as uuidv4 } from 'uuid'

// common
import Layout from './common/Layout'
import LayoutNoLogin from './common/Layout/NoLogin'

// page
import Login from './page/Login'
import LoginCallback from './page/Login/Callback'
import Service from './page/Service'
import ServiceDetail from './page/Service/Detail'
import News from './page/News'
import Pay from './page/Pay'
import PayEdit from './page/Pay/Edit'
import Id from './page/Id'
import IdEdit from './page/Id/Edit'
import Account from './page/Account'
import AccountEdit from './page/Account/Edit'
import AccountEditUser from './page/Account/Edit/User'
import AccountEditContractor from './page/Account/Edit/Contractor'
import AccountEditComplete from './page/Account/Edit/Complete'
import Password from './page/Password'
import PasswordSendmail from './page/Password/Sendmail'
import PasswordReset from './page/Password/Reset'
import PasswordResetComplate from './page/Password/Reset/Complate'
import TanakaIdConfirm from './page/TanakaId/Confirm'
import TanakaIdSwitch from './page/TanakaId/Switch'
import Registration from './page/Registration'
import PurchaseHistories from './page/Purchases/Histories'
import PurchaseDetail from './page/Purchases/Detail'
import Home from './page/Home'
import Result3dsPage from './page/Callback/3ds-result'
import ProductInfoPage from './page/ProductInfo'

// const
import { Color, Size } from 'const'

// hooks
import { useLoginHook } from 'hooks'

// util
import { useGtmLoadTracking } from 'utils/gtm'

const PrivateRoute = ({ children, path }) => {
  const { isAuthenticated } = useLoginHook()

  return (
    <Route
      path={path}
      render={({ location }) =>
        isAuthenticated ? children : <Redirect to={{ pathname: '/login', state: { from: location } }} />
      }
    />
  )
}

const MainRoute = () => {
  useGtmLoadTracking()

  return (
    <Switch>
      <Route exact path="/">
        <Redirect to={{ pathname: '/login' }} />
      </Route>
      <Route path="/login/old" render={(props) => <Login qs={parse(props.location.search)} />} />
      <Route
        path="/login/callback"
        render={(props) => <LayoutNoLogin page={<LoginCallback qs={parse(props.location.search)} />} noMenu={true} />}
      />
      <Route
        path="/login"
        component={() => {
          const state = uuidv4().replace(/-/g, '')
          const nonce = uuidv4().replace(/-/g, '')
          const url =
            process.env.REACT_APP_SSO_LOGIN_URL +
            encodeURIComponent(
              window.location.protocol + '//' + window.location.host + '/ninal/mypage/login/callback'
            ) +
            '&state=' +
            state +
            '&nonce=' +
            nonce

          window.location.replace(url)
        }}
      />
      <Route
        path="/tanakaid/switch"
        render={() => <Layout page={<TanakaIdSwitch />} title="IDの再登録をお願いします" noMenu={true} noLink={true} />}
      />
      <Route
        path="/tanakaid/confirm/:token"
        render={() => <LayoutNoLogin page={<TanakaIdConfirm />} noMenu={true} />}
      />
      <Route
        path="/password/reset/complate"
        render={() => <LayoutNoLogin page={<PasswordResetComplate />} noMenu={true} />}
      />
      <Route path="/password/reset/:token" render={() => <LayoutNoLogin page={<PasswordReset />} noMenu={true} />} />
      <Route path="/password/sendmail" render={() => <LayoutNoLogin page={<PasswordSendmail />} noMenu={true} />} />
      <Route path="/password" render={() => <LayoutNoLogin page={<Password />} noMenu={true} />} />

      <Route path="/signup" exact render={() => <Layout page={<Registration />} noMenu={true} noLink={true} />} />
      <PrivateRoute path="/service/:id">
        <Layout page={<ServiceDetail />} title="NINAL契約詳細" />
      </PrivateRoute>
      <PrivateRoute path="/service">
        <Layout page={<Service />} title="NINALの契約" />
      </PrivateRoute>
      <PrivateRoute path="/news/:id">
        <Layout page={<News />} title="お知らせ詳細" text="メガネの田中からのご案内や重要なお知らせです。" />
      </PrivateRoute>
      <PrivateRoute path="/news">
        <Layout page={<News />} title="お知らせ一覧" text="メガネの田中からのご案内や重要なお知らせです。" />
      </PrivateRoute>
      <PrivateRoute path="/pay/:id/edit">
        <Layout page={<PayEdit />} title="お支払い方法の登録/変更" />
      </PrivateRoute>
      <PrivateRoute path="/pay/:id">
        <Layout page={<Pay />} title="お支払い状況" />
      </PrivateRoute>
      <PrivateRoute path="/id/edit">
        <Layout page={<IdEdit />} title="TANAKA-IDの編集" />
      </PrivateRoute>
      <PrivateRoute path="/id">
        <Layout
          page={<Id />}
          title="TANAKA-IDの編集"
          text="MyTanakaIDを変更しても、メールアドレスは変更されません。変更をご希望される方は、あわせてメールアドレスの変更操作を行ってください。"
          textStyles={{ color: Color.FONT.DANGER, fontWeight: 600 }}
        />
      </PrivateRoute>
      <PrivateRoute path="/account/edit/user">
        <Layout page={<AccountEditUser />} title="ご利用者情報変更" />
      </PrivateRoute>
      <PrivateRoute path="/account/edit/contractor">
        <Layout page={<AccountEditContractor />} title="ご契約者情報変更" />
      </PrivateRoute>
      <PrivateRoute path="/account/edit/complete">
        <Layout page={<AccountEditComplete />} title="会員情報変更" />
      </PrivateRoute>
      <PrivateRoute path="/account/edit">
        <Layout page={<AccountEdit />} title="お客様情報の編集" text="お客様情報の変更が可能です。" />
      </PrivateRoute>
      <PrivateRoute path="/account">
        <Layout page={<Account />} title="アカウント設定" titleStyles={{ paddingBottom: 0 }} />
      </PrivateRoute>
      <PrivateRoute exact path="/purchases/histories">
        <Layout page={<PurchaseHistories />} title="店で購入" />
      </PrivateRoute>
      <PrivateRoute exact path="/purchases/details">
        <Layout page={<PurchaseDetail />} title="購入商品詳細" />
      </PrivateRoute>
      <PrivateRoute exact path="/top">
        <Layout page={<Home />} />
      </PrivateRoute>
      <PrivateRoute exact path="/:contractId/3ds-result/:transId">
        <Result3dsPage />
      </PrivateRoute>
      <PrivateRoute exact path="/product-info">
        <Layout page={<ProductInfoPage />} />
      </PrivateRoute>
    </Switch>
  )
}

function DesktopApp() {
  return (
    <Router basename="/ninal/mypage/">
      <GlobalStyle />
      <MainRoute />
    </Router>
  )
}

const GlobalStyle = createGlobalStyle`
  ${reset}
  html {
    box-sizing: border-box;
  }
  body {
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    font-family: "游ゴシック体",YuGothic,"游ゴシック Medium","Yu Gothic Medium","游ゴシック","Yu Gothic",-apple-system,BlinkMacSystemFont,"Hiragino Kaku Gothic ProN","Noto Sans JP","メイリオ",Meiryo,Sans-Serif;
    font-size: ${Size.FONT.REGULAR};
    color: ${Color.FONT.MENU_DARK};
    line-height: 1;
    background-color: ${Color.BACKGROUND.NORMAL};
  }
  a {
    text-decoration-line: none;
    color: ${Color.FONT.LINK};
  }
  #main p {
    letter-spacing: 0.15em;
  }
  #main {
    line-height: 1.6;
    padding: 70px 0 0 !important;
    @media screen and (max-width: 1024.99px) {
      padding-top: 17vw !important;
    }
  }
`

PrivateRoute.propTypes = {
  children: PropTypes.node.isRequired,
  path: PropTypes.string.isRequired,
}

export default DesktopApp
